import { useCallback, useRef, useState } from 'react'

import { preprocessForOcr } from './ocrPreprocessing'

type PdfJsModule = typeof import('pdfjs-dist')
type TesseractModule = typeof import('tesseract.js')
type TesseractWorker = Awaited<ReturnType<TesseractModule['createWorker']>>

let pdfJsPromise: Promise<PdfJsModule> | null = null
let tesseractPromise: Promise<TesseractModule> | null = null

async function loadPdfJs(): Promise<PdfJsModule> {
  if (!pdfJsPromise) {
    pdfJsPromise = import('pdfjs-dist').then((pdfjs) => {
      // react-pdf bundles pdfjs v4, but we need v5 for the `canvas` parameter
      // on RenderParameters, so load pdfjs-dist only when OCR runs.
      pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`
      return pdfjs
    })
  }

  return pdfJsPromise
}

async function loadTesseract(): Promise<TesseractModule> {
  if (!tesseractPromise) {
    tesseractPromise = import('tesseract.js')
  }

  return tesseractPromise
}

export interface OcrWord {
  text: string
  bbox: { x0: number; y0: number; x1: number; y1: number }
  /** Tesseract recognition confidence, expressed as a percentage (0–100). */
  confidence: number
}

export type OcrStatus = 'idle' | 'loading' | 'done' | 'error'

export interface PageOcrState {
  status: OcrStatus
  words: readonly OcrWord[]
  /** Width of the canvas Tesseract actually processed */
  ocrSourceWidth: number
  error?: string
}

/**
 * Render at 4× (~288 DPI). Higher resolution helps Tesseract on small text
 * in engineering drawings.
 */
const OCR_RENDER_SCALE = 4

/** Words below this confidence are discarded as noise.
 *  38 catches borderline tokens (short IDs like H2S, 03E111A at line ends)
 *  without adding significant noise on clean scanned documents. The
 *  binarization + line removal keep false positives low. */
const MIN_CONFIDENCE = 38

// ─────────────────────────────────────────────────────────────────────────────
// Word extraction
// ─────────────────────────────────────────────────────────────────────────────

type TessWord = {
  text: string
  confidence: number
  bbox: { x0: number; y0: number; x1: number; y1: number }
}
type TessLine = { words?: TessWord[] }
type TessParagraph = { lines?: TessLine[] }
type TessBlock = { paragraphs?: TessParagraph[] }

function extractWords(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any
): OcrWord[] {
  return ((data.blocks as TessBlock[]) ?? [])
    .flatMap((b) => b.paragraphs ?? [])
    .flatMap((p) => p.lines ?? [])
    .flatMap((l) => l.words ?? [])
    .filter((w) => {
      if (w.confidence <= MIN_CONFIDENCE) return false
      if (!w.text.trim()) return false
      const bboxWidth = w.bbox.x1 - w.bbox.x0
      const bboxHeight = w.bbox.y1 - w.bbox.y0
      if (bboxWidth <= 1 || bboxHeight <= 1) return false
      if (bboxHeight > bboxWidth * 4) return false
      return true
    })
    .map((w) => ({
      text: w.text,
      confidence: w.confidence,
      bbox: { x0: w.bbox.x0, y0: w.bbox.y0, x1: w.bbox.x1, y1: w.bbox.y1 },
    }))
}

// ─────────────────────────────────────────────────────────────────────────────
// PDF page rendering
// ─────────────────────────────────────────────────────────────────────────────

async function renderPageHighRes(
  url: string,
  pageNumber: number
): Promise<HTMLCanvasElement> {
  const pdfjs = await loadPdfJs()
  const pdfDoc = await pdfjs.getDocument(url).promise
  const page = await pdfDoc.getPage(pageNumber)
  const viewport = page.getViewport({ scale: OCR_RENDER_SCALE })

  const canvas = document.createElement('canvas')
  canvas.width = Math.floor(viewport.width)
  canvas.height = Math.floor(viewport.height)

  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Could not get 2D context for OCR canvas')

  await page.render({ canvasContext: ctx, viewport, canvas }).promise
  await pdfDoc.destroy()

  preprocessForOcr(ctx)

  return canvas
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function usePageOcr() {
  const [pageStates, setPageStates] = useState<Map<number, PageOcrState>>(
    new Map()
  )

  const workerRef = useRef<TesseractWorker | null>(null)

  const getOrCreateWorker = useCallback(async () => {
    if (workerRef.current) return workerRef.current
    const { createWorker, PSM } = await loadTesseract()
    const worker = await createWorker('eng', 1, { logger: () => undefined })
    await worker.setParameters({
      /**
       * PSM.AUTO — fully automatic page segmentation.
       * Tesseract analyses the layout itself and picks the best strategy per region.
       * Better than PSM.SPARSE_TEXT for dense text documents (inspection reports,
       * data sheets) because it reads line-by-line and has better recall on short
       * tokens at line edges. Still handles mixed content (tables + prose) well.
       */
      tessedit_pageseg_mode: PSM.AUTO,
    })
    workerRef.current = worker
    return worker
  }, [])

  const setPageState = useCallback((page: number, state: PageOcrState) => {
    setPageStates((prev) => {
      const next = new Map(prev)
      next.set(page, state)
      return next
    })
  }, [])

  const runOcr = useCallback(
    async (url: string, pageNumber: number) => {
      const existing = pageStates.get(pageNumber)
      if (existing?.status === 'done' || existing?.status === 'loading') return

      setPageState(pageNumber, {
        status: 'loading',
        words: [],
        ocrSourceWidth: 0,
      })

      try {
        const canvas = await renderPageHighRes(url, pageNumber)
        const worker = await getOrCreateWorker()
        const { data } = await worker.recognize(canvas, {}, { blocks: true })
        const words = extractWords(data)

        setPageState(pageNumber, {
          status: 'done',
          words,
          ocrSourceWidth: canvas.width,
        })

        // Release the offscreen canvas memory
        canvas.width = 0
        canvas.height = 0
      } catch (err) {
        setPageState(pageNumber, {
          status: 'error',
          words: [],
          ocrSourceWidth: 0,
          error: err instanceof Error ? err.message : 'OCR failed',
        })
      }
    },
    [pageStates, getOrCreateWorker, setPageState]
  )

  const clearAll = useCallback(() => setPageStates(new Map()), [])

  const terminate = useCallback(async () => {
    if (workerRef.current) {
      await workerRef.current.terminate()
      workerRef.current = null
    }
    clearAll()
  }, [clearAll])

  return { pageStates, runOcr, clearAll, terminate }
}
